import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  Plus, 
  Search, 
  Filter, 
  Download, 
  UploadCloud, 
  Package, 
  Layers, 
  PackageCheck, 
  PackageX,
  ChevronDown,
  Settings,
  TrendingUp,
  BarChart3,
  Grid3X3,
  List,
  Eye,
  Edit3,
  MoreHorizontal,
  Star,
  Activity
} from 'lucide-react';
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import SubscriptionPlanTable from '@/components/subscription-plans/SubscriptionPlanTable';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { LoadingScreen } from '@/components/common';

import { Progress } from "@/components/ui/progress";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface SubscriptionPlanFamily {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
}

interface SubscriptionPlanSummary {
  totalPlans: number;
  activePlans: number;
  inactivePlans: number;
  familyCount: number;
}

export default function SubscriptionPlans() {
  const navigate = useNavigate();
  const [selectedFamily, setSelectedFamily] = useState('all');
  const [planFamilies, setPlanFamilies] = useState<SubscriptionPlanFamily[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [summaryData, setSummaryData] = useState<SubscriptionPlanSummary>({
    totalPlans: 0,
    activePlans: 0,
    inactivePlans: 0,
    familyCount: 0
  });
  const [viewMode, setViewMode] = useState<'list'>('list');
  const [searchTerm, setSearchTerm] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('plans');
  const { toast } = useToast();

  const refreshTable = useCallback(() => {
    setRefreshTrigger(prev => prev + 1);
  }, []);

  const fetchPlanFamilies = async () => {
    try {
      const { data, error } = await supabase
        .from('subscription_plan_families')
        .select('*')
        .eq('is_active', true)
        .order('name', { ascending: true });

      if (error) throw error;
      setPlanFamilies(data || []);
      
      // Don't auto-select first family - let user choose or see all
      
      // Update summary data with family count
      setSummaryData(prev => ({
        ...prev,
        familyCount: data?.length || 0
      }));
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Erro ao carregar famílias de planos',
        description: 'Não foi possível carregar as famílias de planos. Por favor, recarregue a página.',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchPlansSummary = async () => {
    try {
      // Get count of all plans
      const { count: totalCount, error: totalError } = await supabase
        .from('subscription_plans')
        .select('*', { count: 'exact', head: true });

      if (totalError) throw totalError;

      // Get count of active plans
      const { count: activeCount, error: activeError } = await supabase
        .from('subscription_plans')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', true);

      if (activeError) throw activeError;

      // Get count of inactive plans
      const { count: inactiveCount, error: inactiveError } = await supabase
        .from('subscription_plans')
        .select('*', { count: 'exact', head: true })
        .eq('is_active', false);

      if (inactiveError) throw inactiveError;

      setSummaryData(prev => ({
        ...prev,
        totalPlans: totalCount || 0,
        activePlans: activeCount || 0,
        inactivePlans: inactiveCount || 0
      }));
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  };

  useEffect(() => {
    fetchPlanFamilies();
    fetchPlansSummary();
  }, [refreshTrigger]);

  if (loading) {
    return <LoadingScreen message="Carregando Planos..." size="x-small" />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Modern Header with Gradient Background */}
      <div className="relative overflow-hidden" style={{ backgroundColor: '#1F1746' }}>
        {/* SVG Background Element */}
        <div className="absolute inset-0 pointer-events-none">
          <svg 
            className="opacity-30"
            width="1690"
            height="1026.5433"
            viewBox="0 0 826.54 502.06" 
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="xMidYMid slice"
          >
            <g transform="scale(1.1) translate(-70, -315)">
              <path d="M0 0h826.54v502.06H0z" fill="none"/>
              <path d="M0 0h826.54v502.06H0z" fill="url(#a)"/>
              <defs>
                <linearGradient id="a" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#6366f1" stopOpacity=".1"/>
                  <stop offset="100%" stopColor="#8b5cf6" stopOpacity=".1"/>
                </linearGradient>
              </defs>
            </g>
          </svg>
        </div>
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-white/5" />
        
        <div className="relative container mx-auto px-6 py-6 pb-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                  <Package className="h-6 w-6 text-white" />
                </div>
        <div>
                  <h1 className="text-2xl font-extrabold text-white">Planos de Assinatura</h1>
                  <p className="text-blue-100 text-base pb-2">
                  Visualize, edite e organize seus planos por família
          </p>
        </div>
              </div>
            </div>

                        <div className="flex items-center gap-2">
              <Button 
                variant="secondary" 
                size="default" 
                onClick={() => navigate('/import-subscriptions-plans')} 
                className="backdrop-blur-sm bg-white/20 border-white/30 text-white hover:bg-white/30 transition-all duration-200"
              >
                <UploadCloud className="h-4 w-4 mr-2" />
                Importar Planos
              </Button>
              <Button 
                size="default" 
                onClick={() => navigate('/subscription-plans/new')} 
                className="bg-white text-gray-900 hover:bg-gray-100 shadow-lg"
              >
                <Plus className="h-4 w-4 mr-2" />
                Novo Plano
              </Button>
            </div>
          </div>
        </div>
      </div>

        <div className="container mx-auto px-6 -mt-4 relative z-10">
          {/* Enhanced Summary Cards with Better Visual Hierarchy */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-blue-50 to-white">
              <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-blue-100 rounded-md">
                      <Package className="h-4 w-4 text-blue-600" />
                    </div>
                    <span className="text-sm font-extrabold text-gray-600">Total de Planos</span>
                  </div>
                  <div className="text-2xl font-extrabold text-gray-900">
                  {summaryData.totalPlans}
                </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    Em {summaryData.familyCount} famílias
                  </div>
                  <div className="flex items-center gap-1">
                    <TrendingUp className="h-3 w-3 text-blue-500" />
                    <span className="text-xs text-green-600 font-extrabold">+12%</span>
            </div>
            </div>
          </CardContent>
        </Card>

          <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-green-50 to-white">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-green-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-green-100 rounded-md">
                    <PackageCheck className="h-4 w-4 text-green-600" />
                  </div>
                  <span className="text-sm font-extrabold text-gray-600">Planos Ativos</span>
                </div>
                <div className="text-2xl font-extrabold text-gray-900">
                {summaryData.activePlans}
              </div>
              </div>
              <div className="space-y-2">
                <Progress 
                  value={summaryData.totalPlans > 0 ? (summaryData.activePlans / summaryData.totalPlans) * 100 : 0} 
                  className="h-1.5"
                />
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    {summaryData.totalPlans > 0 
                      ? `${Math.round((summaryData.activePlans / summaryData.totalPlans) * 100)}% do total` 
                      : 'Nenhum plano'}
                  </div>
                  <div className="flex items-center gap-1">
                    <Activity className="h-3 w-3 text-green-500" />
                    <span className="text-xs text-green-600 font-extrabold">+5%</span>
              </div>
            </div>
            </div>
          </CardContent>
        </Card>

          <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-red-50 to-white">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-red-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-red-100 rounded-md">
                    <PackageX className="h-4 w-4 text-red-600" />
                  </div>
                  <span className="text-sm font-extrabold text-gray-600">Planos Inativos</span>
                </div>
                <div className="text-2xl font-extrabold text-gray-900">
                {summaryData.inactivePlans}
              </div>
              </div>
              <div className="space-y-2">
                <Progress 
                  value={summaryData.totalPlans > 0 ? (summaryData.inactivePlans / summaryData.totalPlans) * 100 : 0} 
                  className="h-1.5"
                />
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    {summaryData.totalPlans > 0 
                      ? `${Math.round((summaryData.inactivePlans / summaryData.totalPlans) * 100)}% do total` 
                      : 'Nenhum plano'}
                  </div>
                  <div className="flex items-center gap-1">
                    <TrendingUp className="h-3 w-3 text-red-500 rotate-180" />
                    <span className="text-xs text-red-600 font-extrabold">-2%</span>
              </div>
            </div>
            </div>
          </CardContent>
        </Card>

          <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-purple-50 to-white">
            <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-purple-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-purple-100 rounded-md">
                    <Layers className="h-4 w-4 text-purple-600" />
                  </div>
                  <span className="text-sm font-extrabold text-gray-600">Famílias</span>
                </div>
                <div className="text-2xl font-extrabold text-gray-900">
                {summaryData.familyCount}
              </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-500">
                  Categorias disponíveis
                </div>
                <div className="flex items-center gap-1">
                  <Star className="h-3 w-3 text-purple-500" />
                  <span className="text-xs text-purple-600 font-extrabold">+3</span>
            </div>
            </div>
          </CardContent>
        </Card>
      </div>

                {/* Enhanced Main Content with Modern Design */}
        <Card className="border shadow-sm bg-white/80 backdrop-blur-sm">
          <CardContent className="p-6">
                        {/* Enhanced Filter Section - All in one line */}
            <div className="flex items-center justify-between gap-4 mb-6">
              <div className="flex items-center gap-4 flex-1">
                <div className="min-w-0 flex-1">
                  <Select value={selectedFamily} onValueChange={setSelectedFamily}>
                    <SelectTrigger className="h-10 bg-white border-gray-200 focus:border-blue-500 focus:ring-blue-500">
                      <SelectValue placeholder="Família de Planos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all" className="py-3">
                        <div>
                          <div className="font-extrabold">Todas as Famílias</div>
                          <div className="text-xs text-gray-500 truncate max-w-48">
                            Mostrar planos de todas as famílias
                          </div>
                        </div>
                      </SelectItem>
                      {planFamilies.map((family) => (
                        <SelectItem key={family.id} value={family.id} className="py-3">
                          <div>
                            <div className="font-extrabold">{family.name}</div>
                            {family.description && (
                              <div className="text-xs text-gray-500 truncate max-w-48">
                                {family.description}
                              </div>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="relative min-w-0 flex-1 max-w-sm">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 text-gray-400 transform -translate-y-1/2" />
                <Input
                    placeholder="Pesquisar planos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 h-10 bg-white border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              <Popover open={isFilterOpen} onOpenChange={setIsFilterOpen}>
                <PopoverTrigger asChild>
                    <Button variant="outline" className="h-10 px-4 border-gray-200 hover:bg-gray-50">
                      <Filter className="h-4 w-4 mr-2" />
                      Filtros
                      {(showInactive) && (
                        <Badge variant="secondary" className="ml-2 h-4 w-4 p-0 text-xs">
                          1
                        </Badge>
                      )}
                      <ChevronDown className="h-3 w-3 ml-2 opacity-50" />
                  </Button>
                </PopoverTrigger>
                  <PopoverContent className="w-80 p-6" align="end">
                    <div className="space-y-6">
                      <div>
                        <h4 className="font-extrabold text-gray-900 mb-1">Filtros Avançados</h4>
                        <p className="text-sm text-gray-500">Personalize a visualização dos planos</p>
                      </div>
                      
                    <Separator />
                    
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="space-y-1">
                            <label className="text-sm font-extrabold text-gray-700">
                              Mostrar planos inativos
                            </label>
                            <p className="text-xs text-gray-500">
                              Incluir planos desativados na listagem
                            </p>
                          </div>
                      <Checkbox 
                        checked={showInactive} 
                        onCheckedChange={(checked) => setShowInactive(!!checked)} 
                      />
                        </div>
                    </div>
                    
                      <div className="flex justify-between pt-4 border-t">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => {
                          setSearchTerm('');
                          setShowInactive(false);
                          setSelectedFamily('all');
                        }}
                      >
                          Limpar Filtros
                      </Button>
                      <Button size="sm" onClick={() => setIsFilterOpen(false)}>
                          Aplicar
                      </Button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </div>

              <div className="flex bg-gray-100 rounded-lg p-1">
                <button
                  onClick={() => setActiveTab('plans')}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-extrabold rounded-md transition-colors ${
                    activeTab === 'plans'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <List className="h-4 w-4" />
                  Planos
                </button>
                <button
                  onClick={() => setActiveTab('details')}
                  className={`flex items-center gap-2 px-4 py-2 text-sm font-extrabold rounded-md transition-colors ${
                    activeTab === 'details'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <Eye className="h-4 w-4" />
                  Detalhes
                </button>
            </div>
          </div>

            {/* Content Area */}
          {planFamilies.length > 0 ? (
              <div className="space-y-6">
                                {activeTab === 'plans' && (
                  <div className="overflow-hidden">
                  <SubscriptionPlanTable 
                    familyId={selectedFamily} 
                    refreshTrigger={refreshTrigger} 
                    onImport={() => navigate('/import-subscriptions-plans')}
                    showInactive={showInactive}
                    searchTerm={searchTerm}
                    viewMode="list"
                  />
                  </div>
                )}
                
                {activeTab === 'details' && selectedFamily && (
                  <Card className="border-0 shadow-lg bg-gradient-to-br from-white to-gray-50">
                    <CardContent className="p-8">
                      {planFamilies.find(f => f.id === selectedFamily)?.description ? (
                        <div className="space-y-6">
                        <div className="space-y-4">
                            <div className="space-y-2">
                              <h3 className="text-2xl font-extrabold text-gray-900">
                                {planFamilies.find(f => f.id === selectedFamily)?.name}
                              </h3>
                              <p className="text-gray-600 leading-relaxed">
                              {planFamilies.find(f => f.id === selectedFamily)?.description}
                            </p>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-4">
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 px-3 py-1">
                              <div className="w-2 h-2 bg-green-500 rounded-full mr-2" />
                              Família Ativa
                            </Badge>
                            <Button variant="outline" size="sm" className="flex items-center gap-2">
                              <Edit3 className="h-4 w-4" />
                              Editar Família
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-16">
                          <div className="space-y-4">
                            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                              <Package className="h-10 w-10 text-gray-400" />
                            </div>
                            <div className="space-y-2">
                              <h3 className="text-lg font-extrabold text-gray-900">
                                Nenhuma descrição disponível
                              </h3>
                              <p className="text-gray-500 max-w-md mx-auto">
                                Esta família de planos não possui uma descrição detalhada configurada.
                              </p>
                            </div>
                            <Button variant="outline" className="mt-4">
                              <Edit3 className="h-4 w-4 mr-2" />
                              Adicionar Descrição
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
                
                {activeTab === 'details' && (!selectedFamily || selectedFamily === 'all') && (
                  <Card className="border-0 shadow-lg bg-gradient-to-br from-white to-gray-50">
                    <CardContent className="p-8">
                      <div className="text-center py-16">
                        <div className="space-y-4">
                          <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                            <Filter className="h-10 w-10 text-gray-400" />
                          </div>
                          <div className="space-y-2">
                            <h3 className="text-lg font-extrabold text-gray-900">
                              Selecione uma Família
                            </h3>
                            <p className="text-gray-500 max-w-md mx-auto">
                              Escolha uma família de planos no filtro acima para ver seus detalhes e descrição.
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            ) : (
              <div className="text-center py-20">
                <div className="space-y-6">
                  <div className="w-24 h-24 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center mx-auto">
                    <Package className="h-12 w-12 text-blue-500" />
                  </div>
                  <div className="space-y-3">
                    <h3 className="text-xl font-extrabold text-gray-900">
                      Nenhuma família de planos encontrada
                    </h3>
                    <p className="text-gray-500 max-w-lg mx-auto leading-relaxed">
                      Para começar, crie famílias de planos para organizar e categorizar 
                      seus diferentes tipos de assinaturas de forma eficiente.
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <Button onClick={() => navigate('/subscription-plans/families/new')} size="lg">
                      <Plus className="h-5 w-5 mr-2" />
                      Criar Primeira Família
                    </Button>
                    <Button variant="outline" size="lg">
                      <Download className="h-5 w-5 mr-2" />
                      Importar Dados
              </Button>
                  </div>
                </div>
            </div>
          )}
        </CardContent>
      </Card>
      </div>
      
      {/* Bottom Spacing */}
      <div className="h-16" />
    </div>
  );
} 